"use client";

import React, { useState, useCallback, useRef } from "react";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import ImageSlider from "../ui/ImageSlider";
import FullscreenModal from "../ui/FullscreenModal";
import { addFavorite, getUser } from "@nusrif/services";
import type { Swiper as SwiperType } from 'swiper';

interface ProductImageGalleryProps {
  productId?: string;
  images: string[];
}

export const ProductImageGallery = React.memo(
  ({ images, productId }: ProductImageGalleryProps) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const mainSwiperRef = useRef<SwiperType | null>(null);

    const handleImageChange = useCallback((index: number) => {
      setCurrentImageIndex(index);
      if (mainSwiperRef.current) {
        mainSwiperRef.current.slideTo(index);
      }
    }, []);

    const handleFavoriteClick = useCallback(() => {
      setIsFavorite((prev) => !prev);
      const userId = getUser()?.id;

      // Add to user favorites
      if (userId && productId) {
        addFavorite({
          userId: userId,
          productId: Number(productId),
        });
      }
    }, []);

    // Handler to open fullscreen at current image
    const handleOpenFullscreen = useCallback(() => {
      setIsFullscreen(true);
    }, []);
    
    const handleCloseFullscreen = useCallback(() => {
      setIsFullscreen(false);
    }, []);

    const goToNextImage = useCallback(() => {
      if (mainSwiperRef.current && images.length > 1) {
        mainSwiperRef.current.slideNext();
      }
    }, [images.length]);

    const goToPreviousImage = useCallback(() => {
      if (mainSwiperRef.current && images.length > 1) {
        mainSwiperRef.current.slidePrev();
      }
    }, [images.length]);

    return (
      <div className="relative w-full">
        {/* Main Image with Swiper */}
        <div
          className="relative w-full h-[400px] bg-gray-100 cursor-zoom-in"
          onClick={handleOpenFullscreen}
        >
          <ImageSlider 
            autoplayDelay={0} 
            loop={false} 
            pagination={false}
            onSlideChange={(swiper: SwiperType) => setCurrentImageIndex(swiper.realIndex)}
            onSwiper={(swiper: SwiperType) => {
              mainSwiperRef.current = swiper;
            }}
          >
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Product image ${index + 1}`}
                className="object-contain bg-white w-full h-full"
              />
            ))}
          </ImageSlider>
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white p-0 shadow-sm z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPreviousImage();
                }}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white p-0 shadow-sm z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextImage();
                }}
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </Button>
            </>
          )}

          {/* Favorite Button */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/80 hover:bg-white p-0 shadow-sm z-10"
            onClick={(e) => {
              e.stopPropagation();
              handleFavoriteClick();
            }}
          >
            <Heart
              className={`w-5 h-5 ${isFavorite ? "fill-warning-red text-warning-red" : "text-gray-600"}`}
            />
          </Button>

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
        
        {/* Image Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 px-5 py-4 overflow-x-auto scrollbar-hide">
            {images.map((image, index) => (
              <button
                key={index}
                className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  index === currentImageIndex
                    ? "border-primary-blue shadow-sm"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => handleImageChange(index)}
              >
                <img
                  src={image}
                  alt={`Product image ${index + 1}`}
                  className="object-cover w-full h-full"
                />
              </button>
            ))}
          </div>
        )}
        
        {/* Fullscreen Modal with Swiper */}
        <FullscreenModal isOpen={isFullscreen} onClose={handleCloseFullscreen}>
          <div className="w-full h-full flex items-center justify-center relative">
            <ImageSlider autoplayDelay={0} loop={false} pagination={true}>
              {images.map((image, idx) => (
                <img
                  key={idx}
                  src={image}
                  alt={`Product image ${idx + 1}`}
                  className="object-contain w-full h-[80vh] bg-black"
                  style={{ maxHeight: "80vh" }}
                />
              ))}
            </ImageSlider>
            
            {/* Image Counter in Fullscreen */}
            {images.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-lg">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </FullscreenModal>
      </div>
    );
  }
);

ProductImageGallery.displayName = "ProductImageGallery";
